import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ACTIVE_PROMOTION } from '@shared/graphql/queries/promotions';
import { GET_CURRENT_USER_PROMO } from '@shared/graphql/queries/user';
import { useApiClient } from './useApiClient';

interface ActivePromotion {
  id: string;
  source: string;
  discountCode: string;
  title: string;
  description?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface UsePromoTrackingReturn {
  promoSource: string | null;
  promotion: ActivePromotion | null;
  loading: boolean;
  error: string | null;
  setPromoSource: (source: string) => void;
  clearPromoSource: () => void;
  getPromoSourceForRegistration: () => string | undefined;
  getUserDiscountCode: () => string | null;
}

// Map promo sources to discount codes
const PROMO_SOURCE_TO_DISCOUNT_CODE: Record<string, string> = {
  'og_promo': 'OG50',
  'eaglefarm2025V1': 'EAGLEFARM'
};

/**
 * Hook for tracking promotional campaign sources from backend-configured promotions
 * For anonymous users: Sets promo source from active promotions (preserves /promo page functionality)
 * For logged-in users: Prioritizes database promo_source over session storage
 */
export const usePromoTracking = (): UsePromoTrackingReturn => {
  const [promoSource, setPromoSourceState] = useState<string | null>(null);
  const [promotion, setPromotion] = useState<ActivePromotion | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [userLoading, setUserLoading] = useState<boolean>(false);
  const [userError, setUserError] = useState<any>(null);
  
  // Use the API client hook for direct GraphQL calls (same as AuthContext)
  const { client, getClient } = useApiClient();
  
  // Use GraphQL query to fetch active promotion
  const { data, loading, error: gqlError } = useQuery(GET_ACTIVE_PROMOTION, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true
  });

  // Check if user is logged in
  const isLoggedIn = userData?.me?.id;
  
  // Fetch user data using direct GraphQL call (same approach as AuthContext)
  useEffect(() => {
    const fetchUserData = async () => {
      // CRITICAL FIX: Only fetch user data if we have an authenticated client
      if (!client) {
        console.log('🔍 usePromoTracking: No authenticated client available, skipping user data fetch');
        setUserLoading(false);
        return;
      }

      try {
        setUserLoading(true);
        setUserError(null);
        
        console.log('🔍 usePromoTracking: Attempting to fetch user data with direct GraphQL call...');
        
        // GraphQL query to get user profile (same as AuthContext)
        const getUserQuery = `
          query GetUser {
            me {
              id
              promoSource
            }
          }
        `;
        
        console.log('🔍 usePromoTracking: Executing direct GraphQL query...');
        
        // Execute the query using direct GraphQL call (same as AuthContext)
        const response: any = await client.graphql({
          query: getUserQuery
        });
        
        console.log('🔍 usePromoTracking: Direct GraphQL response:', response);
        
        // Update the user data
        if (response?.data?.me) {
          console.log('✅ usePromoTracking: Successfully fetched user data:', response.data.me);
          setUserData(response.data);
        } else {
          console.log('❌ usePromoTracking: No user data in response');
          setUserData(null);
        }
      } catch (error: any) {
        console.error('❌ usePromoTracking: Error fetching user data with direct GraphQL:', error);
        setUserError(error);
        setUserData(null);
      } finally {
        setUserLoading(false);
      }
    };
    
    // CRITICAL FIX: Only fetch if we have an authenticated client AND haven't already fetched
    if (client && !userData && !userLoading) {
      console.log('🔍 usePromoTracking: Client available and no user data yet, fetching...');
      fetchUserData();
    } else if (!client) {
      console.log('🔍 usePromoTracking: No client available (user not authenticated), skipping user data fetch');
      setUserLoading(false);
    }
  }, [client, userData, userLoading]);

  // Process active promotion data
  useEffect(() => {
    if (data?.getActivePromotion) {
      console.log('🎯 Promo tracking: Active promotion loaded:', data.getActivePromotion);
      setPromotion(data.getActivePromotion);
    } else if (!loading && !gqlError) {
      console.log('🎯 Promo tracking: No active promotion found');
      setPromotion(null);
    }
  }, [data, loading, gqlError]);

  // Handle promo source setting based on user authentication status
  useEffect(() => {
    if (userLoading) return; // Wait for user data to load

    if (isLoggedIn) {
      // LOGGED-IN USER: Check database promo_source first
      const databasePromoSource = userData?.me?.promoSource;
      
      if (databasePromoSource) {
        console.log('🎯 Promo tracking (logged in): Using database promo_source:', databasePromoSource);
        setPromoSourceState(databasePromoSource);
        // Update local storage to match database
        if (typeof window !== 'undefined') {
          localStorage.setItem('promo_source', databasePromoSource);
        }
      } else {
        // No database promo_source, check local storage
        const sessionPromoSource = typeof window !== 'undefined' ? localStorage.getItem('promo_source') : null;
        if (sessionPromoSource) {
          console.log('🎯 Promo tracking (logged in): Using session promo_source:', sessionPromoSource);
          setPromoSourceState(sessionPromoSource);
        }
      }
    } else {
      // ANONYMOUS USER: Only check local storage, don't auto-set from active promotion
      // The /promo page should explicitly set the promo source when visited
      const sessionPromoSource = typeof window !== 'undefined' ? localStorage.getItem('promo_source') : null;
      if (sessionPromoSource) {
        console.log('🎯 Promo tracking (anonymous): Using session promo_source:', sessionPromoSource);
        setPromoSourceState(sessionPromoSource);
      }
    }
  }, [userData, userLoading, isLoggedIn, data, loading, gqlError]);

  const setPromoSource = (source: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('promo_source', source);
    }
    setPromoSourceState(source);
    console.log(`🎯 Promo tracking: Set source to ${source}`);
  };

  const clearPromoSource = () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('promo_source');
    }
    setPromoSourceState(null);
    console.log('🎯 Promo tracking: Cleared source');
  };

  const getPromoSourceForRegistration = (): string | undefined => {
    // Only return local storage value - no fallback to active promotion
    // This ensures promotions only apply to users who explicitly visited /promo page
    if (typeof window !== 'undefined') {
      return localStorage.getItem('promo_source') || undefined;
    }
    return undefined;
  };

  // Convert GraphQL error to string
  const error = gqlError ? 'Unable to load promotion' : null;

  const getUserDiscountCode = (): string | null => {
    // For logged-in users, check their promo_source and map to discount code
    const userPromoSource = userData?.me?.promoSource;
    
    console.log('🔍 getUserDiscountCode() DEBUG:');
    console.log('  - userData:', userData);
    console.log('  - userData?.me:', userData?.me);
    console.log('  - userPromoSource:', userPromoSource);
    console.log('  - PROMO_SOURCE_TO_DISCOUNT_CODE:', PROMO_SOURCE_TO_DISCOUNT_CODE);
    console.log('  - userLoading:', userLoading);
    console.log('  - userError:', userError);
    console.log('  - isLoggedIn:', isLoggedIn);
    
    if (userError) {
      console.log('❌ GraphQL Error fetching user data:', userError);
    }
    
    if (userPromoSource && PROMO_SOURCE_TO_DISCOUNT_CODE[userPromoSource]) {
      const discountCode = PROMO_SOURCE_TO_DISCOUNT_CODE[userPromoSource];
      console.log('✅ User discount code found:', userPromoSource, '→', discountCode);
      return discountCode;
    }
    
    console.log('❌ No user discount code found - returning null');
    // If no user promo source, return null (no automatic discount)
    return null;
  };

  return {
    promoSource,
    promotion,
    loading: loading || userLoading,
    error,
    setPromoSource,
    clearPromoSource,
    getPromoSourceForRegistration,
    getUserDiscountCode
  };
};